import React, { useState } from 'react';
import { Card, Table, Tag, Empty, Tabs, Avatar, Row, Col, Statistic } from 'antd';
import { 
  TrophyOutlined, 
  RiseOutlined, 
  FallOutlined,
  UserOutlined,
  StarOutlined
} from '@ant-design/icons';
import moment from 'moment';
import './PartnerStats.css';

const { TabPane } = Tabs;

/**
 * 双打搭档统计组件
 * 展示搭档统计列表和排行榜
 */
const PartnerStats = ({ partners, loading }) => {
  const [activeRanking, setActiveRanking] = useState('mostMatches');

  if (loading) {
    return (
      <div className="partner-stats loading">
        <div className="loading-text">加载中...</div>
      </div>
    );
  }

  if (!partners || !partners.all || partners.all.length === 0) {
    return (
      <div className="partner-stats empty">
        <Empty description="暂无搭档统计数据" />
      </div>
    );
  }

  const { all: partnerList, rankings } = partners;

  // 表格列定义
  const columns = [
    {
      title: '搭档',
      dataIndex: 'partnerName',
      key: 'partnerName',
      render: (text, record) => (
        <div className="partner-info">
          <Avatar 
            src={record.partnerAvatar} 
            icon={<UserOutlined />}
            size="small"
            className="partner-avatar"
          />
          <span className="partner-name">{text}</span>
        </div>
      ),
      width: 120
    },
    {
      title: '合作场次',
      dataIndex: 'matchesTogether',
      key: 'matchesTogether',
      align: 'center',
      sorter: (a, b) => a.matchesTogether - b.matchesTogether,
      width: 100
    },
    {
      title: '胜场',
      dataIndex: 'winsTogether',
      key: 'winsTogether',
      align: 'center',
      render: (value) => <span style={{ color: '#52c41a' }}>{value}</span>,
      width: 80
    },
    {
      title: '负场',
      dataIndex: 'lossesTogether',
      key: 'lossesTogether',
      align: 'center',
      render: (value) => <span style={{ color: '#ff4d4f' }}>{value}</span>,
      width: 80
    },
    {
      title: '胜率',
      dataIndex: 'winRateTogether',
      key: 'winRateTogether',
      align: 'center',
      render: (value) => (
        <Tag color={value >= 50 ? 'success' : 'default'}>
          {value}%
        </Tag>
      ),
      sorter: (a, b) => a.winRateTogether - b.winRateTogether,
      width: 100
    },
    {
      title: '净得分',
      dataIndex: 'netPoints',
      key: 'netPoints',
      align: 'center',
      render: (value) => (
        <span style={{ color: value >= 0 ? '#52c41a' : '#ff4d4f' }}>
          {value >= 0 ? '+' : ''}{value}
        </span>
      ),
      sorter: (a, b) => a.netPoints - b.netPoints,
      width: 100
    },
    {
      title: '平均净得分',
      dataIndex: 'avgNetPoints',
      key: 'avgNetPoints',
      align: 'center',
      render: (value) => (
        <span style={{ color: value >= 0 ? '#52c41a' : '#ff4d4f' }}>
          {value >= 0 ? '+' : ''}{value.toFixed(1)}
        </span>
      ),
      sorter: (a, b) => a.avgNetPoints - b.avgNetPoints,
      width: 120
    },
    {
      title: '赢分',
      dataIndex: 'ratingGained',
      key: 'ratingGained',
      align: 'center',
      render: (value) => (
        <span style={{ color: '#52c41a', fontWeight: 'bold' }}>
          +{value || 0}
        </span>
      ),
      sorter: (a, b) => (a.ratingGained || 0) - (b.ratingGained || 0),
      width: 100
    },
    {
      title: '输分',
      dataIndex: 'ratingLost',
      key: 'ratingLost',
      align: 'center',
      render: (value) => (
        <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
          -{value || 0}
        </span>
      ),
      sorter: (a, b) => (a.ratingLost || 0) - (b.ratingLost || 0),
      width: 100
    },
    {
      title: '净积分',
      dataIndex: 'netRatingChange',
      key: 'netRatingChange',
      align: 'center',
      render: (value) => {
        const netValue = value || 0;
        return (
          <span style={{ 
            color: netValue >= 0 ? '#52c41a' : '#ff4d4f',
            fontWeight: 'bold'
          }}>
            {netValue >= 0 ? '+' : ''}{netValue}
          </span>
        );
      },
      sorter: (a, b) => (a.netRatingChange || 0) - (b.netRatingChange || 0),
      width: 100
    }
  ];

  // 排行榜卡片组件
  const RankingCard = ({ title, icon, data, sortKey, formatValue }) => {
    if (!data || data.length === 0) {
      return (
        <Card className="ranking-card">
          <Empty description="暂无数据" size="small" />
        </Card>
      );
    }

    return (
      <Card className="ranking-card" title={title}>
        <div className="ranking-list">
          {data.map((partner, index) => (
            <div key={partner.partnerId} className="ranking-item">
              <div className="ranking-rank">
                <span className={`rank-badge rank-${index + 1}`}>
                  {index + 1}
                </span>
              </div>
              <Avatar 
                src={partner.partnerAvatar} 
                icon={<UserOutlined />}
                size="small"
                className="ranking-avatar"
              />
              <div className="ranking-info">
                <div className="ranking-name">{partner.partnerName}</div>
                <div className="ranking-value">
                  {formatValue ? formatValue(partner[sortKey]) : partner[sortKey]}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    );
  };

  // 排行榜Tab配置
  const rankingTabs = [
    {
      key: 'mostMatches',
      label: '组队最多',
      icon: <TrophyOutlined />,
      data: rankings.mostMatches || [],
      sortKey: 'matchesTogether',
      formatValue: (value) => `${value}场`
    },
    {
      key: 'highestWinRate',
      label: '胜率最高',
      icon: <RiseOutlined />,
      data: rankings.highestWinRate || [],
      sortKey: 'winRateTogether',
      formatValue: (value) => `${value}%`
    },
    {
      key: 'lowestWinRate',
      label: '胜率最低',
      icon: <FallOutlined />,
      data: rankings.lowestWinRate || [],
      sortKey: 'winRateTogether',
      formatValue: (value) => `${value}%`
    },
    {
      key: 'bestNetPoints',
      label: '净得分最高',
      icon: <StarOutlined />,
      data: rankings.bestNetPoints || [],
      sortKey: 'avgNetPoints',
      formatValue: (value) => `${value >= 0 ? '+' : ''}${value.toFixed(1)}`
    }
  ];

  return (
    <div className="partner-stats">
      <div className="stats-header">
        <h3 className="stats-title">搭档分析</h3>
        <div className="stats-summary">
          共 {partnerList.length} 位搭档
        </div>
      </div>

      {/* 排行榜区域 */}
      <div className="rankings-section">
        <Tabs 
          activeKey={activeRanking} 
          onChange={setActiveRanking}
          type="card"
          className="ranking-tabs"
        >
          {rankingTabs.map(tab => (
            <TabPane 
              tab={
                <span>
                  {tab.icon} {tab.label}
                </span>
              } 
              key={tab.key}
            >
              <RankingCard
                title={`${tab.label} Top 5`}
                icon={tab.icon}
                data={tab.data}
                sortKey={tab.sortKey}
                formatValue={tab.formatValue}
              />
            </TabPane>
          ))}
        </Tabs>
      </div>

      {/* 完整搭档列表 */}
      <div className="partner-list-section">
        <Card title="所有搭档" className="partner-list-card">
          <Table
            columns={columns}
            dataSource={partnerList.map((p, index) => ({ ...p, key: p.partnerId || index }))}
            pagination={{
              pageSize: 10,
              showSizeChanger: false,
              showTotal: (total) => `共 ${total} 位搭档`
            }}
            scroll={{ x: 'max-content' }}
            size="small"
          />
        </Card>
      </div>
    </div>
  );
};

export default PartnerStats;

